import { FGSelectInput, FieldGroup, FGTextAreaInput } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  ActivityApi,
  ActivityDto,
  ActivityTranslationDto,
} from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { FGMultiTranslate, SmallFormGenerator } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface IActivityDetailsPageProps {
  activityId: number;
  editMode: boolean;
}

export const ActivityDetailsPage: React.FunctionComponent<IActivityDetailsPageProps> = ({
  activityId,
  editMode,
}) => {
  const { t } = useTl();
  const api = useApiService(ActivityApi);
  const history = useHistory();
  const { languages, currentLanguage } = useGlobalData();
  const [sections, sLoading] = useReferential((a) => a.getSections(), true, [
    currentLanguage,
  ]);

  const {
    data,
    loading,
    deleteItem,
    deleting,
    saveItem,
    saving,
  } = useCrudApi<ActivityDto>(
    {
      getApiFn: (id = activityId) =>
        id > 0
          ? api.get(id)
          : new ActivityDto({
              activityTranslations: [
                ...languages.map(
                  (l) =>
                    new ActivityTranslationDto({ languageId: l.languageId })
                ),
              ],
            }).toJSON(),
      saveApiFn: (d) => api.save(d),
      onSavedRoute: (d) => `${ERoutes.activity}/${d.activityId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.activityId),
      onDeletedRoute: () => ERoutes.activity,
    },
    [activityId]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      sectionId: Yup.number().nullable().required(t(ETLCodes.Required)),
      activityTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
    });
  }, [t]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.activity)}
        onDelete={deleteItem}
        showDeleteButton={activityId > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup>
          <FGMultiTranslate
            label={ETLCodes.Name}
            translationDto={ActivityTranslationDto}
            translationField="text"
            translationsArrayField="activityTranslations"
          />
        </FieldGroup>
        <FieldGroup>
          <FGSelectInput
            items={sections}
            loading={sLoading}
            name="sectionId"
            label={t(ETLCodes.Section)}
          />
          <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
